import { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { scrollToId } from "../../lib/scroll";
import { RunIcon } from "../RunIcon";

const TEAMVIEWER_URL = "https://get.teamviewer.com/mfmedgmbh";

const NAV = [
  { id: "leistungen", label: "Leistungen" },
  { id: "zielgruppen", label: "Zielgruppen" },
  { id: "kontakt", label: "Kontakt" },
];

export default function Header({ contact, logoHeight, logoHeightMobile, bgColor, dark }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [vw, setVw] = useState(typeof window !== "undefined" ? window.innerWidth : 1280);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onResize); };
  }, []);

  const go = (id) => { setOpen(false); scrollToId(id); };
  const dial = contact?.phone_dial || "+4962515504920";

  // Direkthilfe: TeamViewer in einem kleinen Popup-Fenster öffnen (Seite bleibt erhalten,
  // der Download startet dort automatisch). Fallback: neuer Tab, falls das Popup blockiert wird.
  const openDirekthilfe = (e) => {
    e.preventDefault();
    setOpen(false);
    const w = 460, h = 600;
    const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
    const win = window.open(
      TEAMVIEWER_URL,
      "mfmed-direkthilfe",
      `popup=yes,width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
    if (!win || win.closed || typeof win.closed === "undefined") {
      window.open(TEAMVIEWER_URL, "_blank", "noopener"); // Popup blockiert → neuer Tab
    }
  };

  // The logo PNG has its own red vertical line at ~41.7% of its width. Shift the
  // logo left so that line lands exactly on the page's red rail. The rail/container
  // padding differ per breakpoint (below 1024px: rail 24px, pad 36px → const 11;
  // desktop ≥1024px: rail 40px, pad 56px → const 15). The smaller "mobile" logo
  // height is used for the ENTIRE sub-desktop range (<1024px) so it stays aligned
  // and never overflows on tablets/phones; desktop uses the larger height.
  const isLg = vw >= 1024;
  const lh = isLg ? (logoHeight || 56) : (logoHeightMobile || 40);
  const containerLeft = Math.max(0, (vw - 1260) / 2);
  const pad = isLg ? 56 : 36; // .mf-container padding-left per breakpoint
  const rawMl = -((isLg ? 15 : 11) + 0.417 * lh);
  // Safety clamp: never let the logo overflow the left screen edge (keep >= 2px in).
  const logoMl = Math.max(rawMl, -(containerLeft + pad - 2));

  return (
    <header
      data-testid="site-header"
      style={{ backgroundColor: bgColor || "#ffffff" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-md ${
        scrolled ? "border-b border-black/5 shadow-sm" : ""
      } ${dark ? "mf-section-dark" : ""}`}
    >
      <div className="mf-container flex items-center justify-between min-h-[72px]">
        <a href="/" data-testid="header-logo" style={{ marginLeft: `${logoMl}px` }} className="flex items-center gap-3 shrink-0">
          <img src="/mfmed-logo.png" alt="MF MED GmbH Logo" style={{ height: `${lh}px` }} className="w-auto" />
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-head font-bold tracking-tight text-[15px] text-[var(--mf-ink)]">MF MED GmbH</span>
            <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--mf-grey)]">… für Sie unterwegs …</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => go(n.id)}
              className="font-head text-sm font-semibold text-[var(--mf-ink-soft)] hover:text-[var(--mf-ink)] mf-link-underline"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={TEAMVIEWER_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={openDirekthilfe}
            data-testid="header-direkthilfe-btn"
            className="hidden sm:inline-flex items-center gap-2 border border-[var(--mf-line)] text-[var(--mf-ink)] font-head text-sm font-semibold py-2.5 px-4 hover:bg-[var(--mf-mist)] transition-colors"
          >
            <RunIcon size={16} />
            Direkthilfe
          </a>
          <a href={`tel:${dial}`} data-testid="header-call-btn" className="mf-cta text-sm py-2.5 px-4">
            <Phone size={16} strokeWidth={2.4} />
            <span className="hidden sm:inline">{contact?.phone || "Anrufen"}</span>
            <span className="sm:hidden">Anrufen</span>
          </a>
          <button
            className="lg:hidden p-2 text-[var(--mf-ink)]"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menü"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-[var(--mf-line)]" data-testid="mobile-menu">
          <div className="mf-container py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <button
                key={n.id}
                onClick={() => go(n.id)}
                data-testid={`mobile-nav-${n.id}`}
                className="text-left font-head font-semibold py-3 border-b border-[var(--mf-line)] text-[var(--mf-ink)]"
              >
                {n.label}
              </button>
            ))}
            <a
              href={TEAMVIEWER_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={openDirekthilfe}
              data-testid="mobile-nav-direkthilfe"
              className="flex items-center gap-2 font-head font-semibold py-3 text-[var(--mf-red)]"
            >
              <RunIcon size={18} />
              Direkthilfe
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
